import { useEffect, useMemo } from 'react';

import { useGetCollectionOrderReports } from 'api';
import type { OrderReports } from 'api';

import { useOrganisationId, useVirtualAccounts } from 'hooks/context';
import { useGlobalFilters } from 'hooks/useGlobalFilters';
import { useVirtualList } from 'hooks/useVirtualList';

import { DataTable } from 'components/ui/organisms/DataTable';

import { apiParams } from './apiAdapter';
import { getColumns } from './columns';
import type { LocalState } from './localState';

import { EXPORTED_PAYINS_TABLE_TEST_ID } from '../constants';

type ExportedPayinsTableProps = {
  localState: LocalState;
  updateCursorAfter: (value: string | null) => void;
  downloadReport: (report: OrderReports) => void;
};

export const ExportedPayinsTable = ({
  localState,
  updateCursorAfter,
  downloadReport
}: ExportedPayinsTableProps) => {
  const organisationId = useOrganisationId<number>();

  const { filters: globalFilters } = useGlobalFilters();

  const { findAccountByAccountNumber } = useVirtualAccounts();

  const { data: exportedPayins, isFetching } = useGetCollectionOrderReports(
    organisationId,
    apiParams({ ...localState, ...globalFilters }),
    { query: { refetchOnMount: true } }
  );

  useEffect(() => {
    updateCursorAfter(null);
  }, [globalFilters, localState.filters, updateCursorAfter]);

  const exportedPayinsColumns = useMemo(
    () => getColumns(findAccountByAccountNumber, downloadReport),
    [findAccountByAccountNumber, downloadReport]
  );

  const { onScroll, data } = useVirtualList<OrderReports>({
    data: exportedPayins?.data,
    uniqueKey: 'uuid',
    isFetching,
    initialRefValue: String(exportedPayins?.meta?.cursor_next),
    updateCursorCallback: updateCursorAfter,
    resetConditions: [globalFilters, localState.filters, updateCursorAfter],
    cursorNext: exportedPayins?.meta?.has_next_page
      ? String(exportedPayins?.meta?.cursor_next)
      : undefined
  });

  return (
    <DataTable
      total={exportedPayins?.meta?.total_count}
      dataSource={data}
      columns={exportedPayinsColumns}
      onScroll={onScroll}
      isLoading={isFetching}
      rowKey="uuid"
      tableDataTestId={EXPORTED_PAYINS_TABLE_TEST_ID}
    />
  );
};
